import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ExternalLinkIcon } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SelectInput,
  type DataTableSort,
  useFromHere,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';
import { LIVE_REFRESH_MS, money, periodLabel, whenLabel } from '@/lib/report-window';
import { useTableSort } from '@/lib/use-table-sort';
import {
  CLASSIFICATION_REASON,
  LEAD_CLASSIFICATION,
  LEAD_OUTCOME,
  LEAD_SORT_KEYS,
  STATS_INTERVAL,
  leadPath,
  listLeads,
  salesforceLeadUrl,
  setLeadAnalyticsOverride,
  type ClassificationReason,
  type Lead,
  type LeadClassification,
  type LeadOutcome,
} from '@/lib/leads';

/** Leads per page in the table. */
const LEADS_PAGE_SIZE = 25;

/** Newest first, unless the URL says otherwise. */
const DEFAULT_LEAD_SORT: DataTableSort = { key: 'createdAt', direction: 'desc' };

type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/** How each outcome reads in the table. */
const OUTCOME_LABELS: Readonly<Record<LeadOutcome, { label: string; tone: Tone }>> = {
  signed: { label: 'Signed', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'warning' },
  open: { label: 'In proposal', tone: 'info' },
  lost: { label: 'Lost', tone: 'danger' },
  unqualified: { label: 'Unqualified', tone: 'neutral' },
  none: { label: 'No opportunity', tone: 'neutral' },
};

/** An open opportunity that has not had a proposal yet. */
const OPEN_BEFORE_PROPOSAL = { label: 'Being worked', tone: 'info' } as const;

/** How each classification reads in the table. */
const CLASSIFICATION_LABELS: Readonly<Record<LeadClassification, { label: string; tone: Tone }>> = {
  [LEAD_CLASSIFICATION.counted]: { label: 'Counted', tone: 'success' },
  [LEAD_CLASSIFICATION.junk]: { label: 'Junk', tone: 'danger' },
  [LEAD_CLASSIFICATION.duplicate]: { label: 'Duplicate', tone: 'neutral' },
  [LEAD_CLASSIFICATION.neverWorked]: { label: 'Never worked', tone: 'warning' },
  [LEAD_CLASSIFICATION.test]: { label: 'Test', tone: 'neutral' },
};

/** Why a lead got its classification, in the reader's words. A counted lead needs no reason. */
const REASON_LABELS: Readonly<Record<ClassificationReason, string>> = {
  [CLASSIFICATION_REASON.testCompany]: 'Company is Daily Test',
  [CLASSIFICATION_REASON.internalEmail]: 'Sent from a staff email',
  [CLASSIFICATION_REASON.closedDuplicate]: 'Sales closed it as a duplicate',
  [CLASSIFICATION_REASON.repeatInquiry]: 'Same email asked again within 30 days',
  [CLASSIFICATION_REASON.leadStatus]: 'Sales marked it spam or unqualified',
  [CLASSIFICATION_REASON.closedUnqualified]: 'Sales closed it as unqualified',
  [CLASSIFICATION_REASON.notConverted]: 'Not worked after 30 days',
  [CLASSIFICATION_REASON.realLead]: '',
};

/** The table's "Show" filter: every lead, by whether it counts, or by one classification. */
const SHOW = {
  all: 'all',
  counted: 'counted',
  notCounted: 'not_counted',
  junk: LEAD_CLASSIFICATION.junk,
  duplicate: LEAD_CLASSIFICATION.duplicate,
  neverWorked: LEAD_CLASSIFICATION.neverWorked,
  test: LEAD_CLASSIFICATION.test,
} as const;
type Show = (typeof SHOW)[keyof typeof SHOW];
const SHOW_OPTIONS: readonly { key: Show; label: string }[] = [
  { key: SHOW.counted, label: 'Counted' },
  { key: SHOW.all, label: 'All' },
  { key: SHOW.notCounted, label: 'Not counted' },
  { key: SHOW.junk, label: 'Junk' },
  { key: SHOW.duplicate, label: 'Duplicates' },
  { key: SHOW.neverWorked, label: 'Never worked' },
  { key: SHOW.test, label: 'Tests' },
];
/** The list opens on counted leads, like the rest of the page; the other views are a click away. */
const DEFAULT_SHOW: Show = SHOW.counted;
const SHOW_PARAM = 'show';
const PAGE_PARAM = 'page';

/** What each "Show" choice asks the API for. */
const SHOW_QUERY: Readonly<Record<Show, { counted?: '1' | '0'; classification?: LeadClassification }>> = {
  [SHOW.all]: {},
  [SHOW.counted]: { counted: '1' },
  [SHOW.notCounted]: { counted: '0' },
  [SHOW.junk]: { classification: LEAD_CLASSIFICATION.junk },
  [SHOW.duplicate]: { classification: LEAD_CLASSIFICATION.duplicate },
  [SHOW.neverWorked]: { classification: LEAD_CLASSIFICATION.neverWorked },
  [SHOW.test]: { classification: LEAD_CLASSIFICATION.test },
};

/** A person's call on one lead: leave it to the rules, or include / exclude it by hand. */
const OVERRIDE = { automatic: 'automatic', include: 'include', exclude: 'exclude' } as const;
type OverrideChoice = (typeof OVERRIDE)[keyof typeof OVERRIDE];
const OVERRIDE_INCLUDE: Readonly<Record<OverrideChoice, boolean | null>> = {
  [OVERRIDE.automatic]: null,
  [OVERRIDE.include]: true,
  [OVERRIDE.exclude]: false,
};

function isShow(value: string | null): value is Show {
  return SHOW_OPTIONS.some((option) => option.key === value);
}

function overrideChoice(lead: Lead): OverrideChoice {
  if (!lead.analyticsOverride) return OVERRIDE.automatic;
  return lead.analyticsOverride.include ? OVERRIDE.include : OVERRIDE.exclude;
}

/** How the lead arrived — the derived channel, with the website form named when there is one. */
export function leadChannel(lead: Pick<Lead, 'channel' | 'leadSource' | 'formCompleted'>): string {
  const channel = lead.channel || lead.leadSource;
  if (channel && lead.formCompleted && channel !== lead.formCompleted) {
    return `${channel} · ${lead.formCompleted}`;
  }
  return channel || lead.formCompleted || '—';
}

/** Where, when and how big the lead's event is, as far as the form said. */
export function leadEventLabel(lead: Lead): string {
  return (
    [
      lead.eventCity,
      lead.eventDate ? periodLabel(lead.eventDate.slice(0, 10), STATS_INTERVAL.day) : '',
      lead.participantCount !== null ? `${lead.participantCount} people` : '',
    ]
      .filter(Boolean)
      .join(' · ') || '—'
  );
}

/** What happened to the lead: its outcome, the contract value once signed, and whether a proposal went out. */
export function LeadStatus({ lead }: { readonly lead: Lead }): React.ReactElement {
  const proposalSent = lead.analytics?.proposalSent === true;
  const openBeforeProposal = lead.outcome === LEAD_OUTCOME.open && lead.analytics !== null && !proposalSent;
  const outcome = openBeforeProposal ? OPEN_BEFORE_PROPOSAL : OUTCOME_LABELS[lead.outcome];
  const detail =
    lead.outcome === LEAD_OUTCOME.signed && lead.booking?.totalCost != null
      ? money.format(lead.booking.totalCost)
      : proposalSent && lead.outcome !== LEAD_OUTCOME.open
        ? 'Proposal sent'
        : '';
  return (
    <>
      <Pill tone={outcome.tone}>{outcome.label}</Pill>
      {detail ? <span className="mt-1 block text-portal-micro text-portal-muted tabular-nums">{detail}</span> : null}
    </>
  );
}

/** Whether the lead counts as a lead, and why: a person's call, else the classification and its reason. */
export function LeadCounts({ lead }: { readonly lead: Lead }): React.ReactElement {
  if (lead.analyticsOverride) {
    return (
      <>
        <Pill tone={lead.analyticsOverride.include ? 'success' : 'neutral'}>
          {lead.analyticsOverride.include ? 'Included by hand' : 'Excluded by hand'}
        </Pill>
        {lead.analyticsOverride.by ? (
          <span className="mt-1 block text-portal-micro text-portal-muted">Set by {lead.analyticsOverride.by}</span>
        ) : null}
      </>
    );
  }
  if (!lead.analytics) return <Pill tone="neutral">Not classified yet</Pill>;
  const reason = REASON_LABELS[lead.analytics.reason];
  return (
    <>
      <Pill tone={CLASSIFICATION_LABELS[lead.analytics.classification].tone}>
        {CLASSIFICATION_LABELS[lead.analytics.classification].label}
      </Pill>
      {reason ? <span className="mt-1 block text-portal-micro text-portal-muted">{reason}</span> : null}
    </>
  );
}

/**
 * Every lead that arrived in the range, with what happened to it, whether it
 * counts as a lead and a per-lead include / exclude call. Search, sort, page
 * and the "Show" filter live in the URL.
 *
 * @param props.window - The range the page is showing (`to` exclusive).
 * @returns The lead list section.
 */
export function LeadsListSection({ window }: { readonly window: { readonly from: string; readonly to: string } }): React.ReactElement {
  const queryClient = useQueryClient();
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();
  const { sort, onSort } = useTableSort(LEAD_SORT_KEYS, DEFAULT_LEAD_SORT);
  const [params, setParams] = useSearchParams();
  const fromHere = useFromHere();
  const shown = params.get(SHOW_PARAM);
  const show: Show = isShow(shown) ? shown : DEFAULT_SHOW;

  function setShow(next: Show): void {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        if (next === DEFAULT_SHOW) nextParams.delete(SHOW_PARAM);
        else nextParams.set(SHOW_PARAM, next);
        nextParams.delete(PAGE_PARAM);
        return nextParams;
      },
      { replace: true },
    );
  }

  const leads = useQuery({
    queryKey: ['leads', 'table', window.from, window.to, debounced, page, sort, show],
    queryFn: ({ signal }) =>
      listLeads(
        {
          from: window.from,
          to: window.to,
          page,
          limit: LEADS_PAGE_SIZE,
          ...(debounced ? { search: debounced } : {}),
          ...(sort ? { sort: sort.key, dir: sort.direction } : {}),
          ...SHOW_QUERY[show],
        },
        signal,
      ),
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });
  const override = useMutation({
    mutationFn: (input: { readonly id: string; readonly include: boolean | null }) =>
      setLeadAnalyticsOverride(input.id, input.include),
    onSuccess: () => {
      // The call moves the counted trend and every lead analytic, not just this row.
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      void queryClient.invalidateQueries({ queryKey: ['lead-stats'] });
    },
  });

  function analyticsControl(lead: Lead): React.ReactElement {
    return (
      <SelectInput
        density="compact"
        className="min-w-32"
        aria-label={`Analytics for ${lead.name || lead.company || 'this lead'}`}
        value={overrideChoice(lead)}
        disabled={override.isPending}
        onChange={(event) => override.mutate({ id: lead.id, include: OVERRIDE_INCLUDE[event.target.value as OverrideChoice] })}
      >
        <option value={OVERRIDE.automatic}>Automatic</option>
        <option value={OVERRIDE.include}>Include</option>
        <option value={OVERRIDE.exclude}>Exclude</option>
      </SelectInput>
    );
  }

  const searching = debounced !== '';

  return (
    <>
      <div className="mt-8 mb-3">
        <h2 className="text-portal-section font-bold text-portal-ink">Leads in this range</h2>
        <p className="text-portal-micro text-portal-muted">
          Leads that arrived in the range, what happened to each and whether it counts as a lead. Opens on counted
          leads; choose All to see every lead. Set Analytics to Include or Exclude to overrule the rules for one lead.
        </p>
      </div>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <SegmentedControl<Show> label="Show" value={show} onChange={setShow} options={SHOW_OPTIONS} />
        <SearchInput
          label="Search leads"
          placeholder="Name, email, company, owner, city, form…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-80 min-w-56"
        />
      </div>
      {override.isError ? (
        <ErrorNotice title="That lead was not updated" message={errorMessage(override.error)} />
      ) : null}
      {leads.isError ? (
        <ErrorNotice title="Leads list unavailable" message={errorMessage(leads.error)} />
      ) : leads.isPending ? (
        <LoadingState label="Loading leads…" />
      ) : leads.data.items.length === 0 ? (
        <Panel>
          <EmptyState
            title={searching || show !== SHOW.all ? 'No leads match' : 'No leads in this range'}
            message={
              searching
                ? 'Try a different search or filter.'
                : show === SHOW.all
                  ? 'Leads appear here as the sync brings them in.'
                  : 'Choose All to see every lead in the range.'
            }
          />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={[
              { label: 'Arrived', sortKey: 'createdAt' },
              { label: 'Lead', sortKey: 'name' },
              { label: 'Company', sortKey: 'company' },
              { label: 'Channel', sortKey: 'channel' },
              { label: 'Event', sortKey: 'eventDate' },
              { label: 'Owner', sortKey: 'owner' },
              { label: 'What happened', sortKey: 'status' },
              'Counts as',
              'Analytics',
              'Salesforce',
            ]}
            visuallyHiddenHeaders={[9]}
            sort={sort}
            onSort={onSort}
            minWidth={1180}
            cards={leads.data.items.map((lead) => ({
              key: lead.id,
              title: lead.name || lead.company || '—',
              ...(lead.email ? { subtitle: lead.email } : {}),
              fields: [
                { label: 'Arrived', value: whenLabel(lead.sfCreatedAt) },
                { label: 'What happened', value: <LeadStatus lead={lead} /> },
                { label: 'Counts as', value: <LeadCounts lead={lead} /> },
                { label: 'Analytics', value: analyticsControl(lead) },
                { label: 'Company', value: lead.company || '—' },
                { label: 'Channel', value: leadChannel(lead) },
                { label: 'Event', value: leadEventLabel(lead) },
                { label: 'Owner', value: lead.ownerName || '—' },
              ],
              action: (
                <Link
                  to={leadPath(lead.id)}
                  state={fromHere}
                  className="inline-flex min-h-10 items-center text-portal-small font-bold text-brand"
                >
                  View lead
                </Link>
              ),
            }))}
          >
            {leads.data.items.map((lead) => (
              <tr key={lead.id} className="border-t border-portal-line align-middle">
                <td className="p-3 whitespace-nowrap text-portal-small text-portal-muted">
                  {whenLabel(lead.sfCreatedAt)}
                </td>
                <td className="p-3 text-portal-small">
                  <Link to={leadPath(lead.id)} state={fromHere} className="block font-bold text-brand hover:underline">
                    {lead.name || lead.company || 'Unnamed lead'}
                  </Link>
                  {lead.email ? (
                    <span className="block text-portal-micro text-portal-muted">{lead.email}</span>
                  ) : null}
                </td>
                <td className="p-3 text-portal-small">{lead.company || '—'}</td>
                <td className="p-3 text-portal-small text-portal-muted">{leadChannel(lead)}</td>
                <td className="p-3 text-portal-small text-portal-muted">{leadEventLabel(lead)}</td>
                <td className="p-3 text-portal-small text-portal-muted">{lead.ownerName || '—'}</td>
                <td className="p-3 text-portal-small">
                  <LeadStatus lead={lead} />
                </td>
                <td className="p-3 text-portal-small">
                  <LeadCounts lead={lead} />
                </td>
                <td className="p-3 text-portal-small">{analyticsControl(lead)}</td>
                <td className="p-3 text-right">
                  <a
                    href={salesforceLeadUrl(lead.salesforceId)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-brand"
                    aria-label={`Open ${lead.name || lead.company} in Salesforce`}
                  >
                    <ExternalLinkIcon className="size-4" aria-hidden="true" />
                  </a>
                </td>
              </tr>
            ))}
          </DataTable>
          <Pagination
            page={leads.data.pagination.page}
            totalPages={leads.data.pagination.totalPages}
            onChange={setPage}
            summary={`${(leads.data.pagination.page - 1) * LEADS_PAGE_SIZE + 1}–${
              (leads.data.pagination.page - 1) * LEADS_PAGE_SIZE + leads.data.items.length
            } of ${leads.data.pagination.total.toLocaleString()}`}
            className="mt-3"
          />
        </>
      )}
    </>
  );
}
